/**
 * Server state for build step 8 — meetings and minutes (packages/contracts meetings.ts, routes.step8 `meetings.*`
 * and `settings.privacy`):
 *   GET /meetings (the Meetings tab, and a client's meetings with client_id: rows + the four stats),
 *   GET /meetings/{id} (the drawer), POST /meetings/document/read (a minutes file → its text, nothing stored)
 *   then POST /meetings, POST /meetings/{id}/confirm, GET /meetings/{id}/media-url (legacy stored audio),
 *   GET /settings/privacy (whether "Log a meeting" asks for consent, and the consent text).
 *
 * Also the prototype's "Find tasks in these minutes" (`mtExtract`, docs/prototype.html L1849): a plain reading of
 * typed minutes for action lines, decisions and who was there. It only PROPOSES — the person ticks, edits and saves.
 */
import { useCallback, useRef } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { routes, type ErrorBody, type Meeting, type MeetingListItem, type ReadMinutesDocumentResponse } from '@amplispot/contracts';
import { ApiFailure, call, newIdempotencyKey } from '../api';
import { refreshAfterWrite, addDays } from './work';

export const mkeys = {
  all: ['meetings'] as const,
  list: (clientId: string | null) => ['meetings', 'list', clientId ?? 'all'] as const,
  one: (id: string) => ['meetings', 'one', id] as const,
  privacy: ['settings', 'privacy'] as const,
};

export function refreshMeetings(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: mkeys.all });
  refreshAfterWrite(qc);
}

/** A recording or a minutes file still being transcribed or read. */
export const isProcessing = (m: Pick<MeetingListItem, 'transcript_state' | 'extraction_state' | 'status'>) =>
  m.status === 'draft' &&
  (m.transcript_state === 'queued' || m.transcript_state === 'running' || m.extraction_state === 'queued' || m.extraction_state === 'running');

/** Prototype chips: "Minutes confirmed" (hot) / "Transcribing" (info) / "Draft minutes" (warm). */
export function statusChip(m: MeetingListItem): [cls: string, label: string] {
  if (m.status === 'confirmed') return ['hot', 'Minutes confirmed'];
  if (isProcessing(m)) return ['info', m.transcript_state === 'queued' || m.transcript_state === 'running' ? 'Transcribing' : 'Reading the minutes'];
  return ['warm', 'Draft minutes'];
}

// ---------------------------------------------------------------- reads

const PAGE = 50;

/** GET /meetings, newest first. "Show more" fetches one more page; the stats come with the first page. */
export function useMeetings(clientId: string | null = null) {
  return useInfiniteQuery({
    queryKey: mkeys.list(clientId),
    queryFn: ({ pageParam }) => call('meetings.list', { query: { limit: PAGE, cursor: pageParam, client_id: clientId ?? undefined } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    // A recording being transcribed comes back on its own: look again every 15 s while one is in flight.
    refetchInterval: (q) => (q.state.data?.pages.some((p) => p.data.some(isProcessing)) ? 15_000 : false),
  });
}

export function useMeeting(id: string | null) {
  return useQuery({
    queryKey: mkeys.one(id ?? ''),
    queryFn: () => call('meetings.get', { params: { id: id! } }),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && isProcessing(q.state.data) ? 15_000 : false),
  });
}

export function usePrivacy(enabled = true) {
  return useQuery({ queryKey: mkeys.privacy, queryFn: () => call('settings.privacy'), enabled, staleTime: 60_000 });
}

// ---------------------------------------------------------------- writes

/**
 * POST /meetings/document/read: the text of a minutes document (multipart; the browser sets the boundary). Read-only:
 * no meeting is made and nothing is stored; Submit is POST /meetings with transcript_source 'document'. A file it
 * cannot use is a 422 on `file` whose message says why (ApiFailure).
 */
export async function readMeetingDocument(file: File, signal?: AbortSignal): Promise<ReadMinutesDocumentResponse> {
  const form = new FormData();
  form.set('file', file, file.name);
  let res: Response;
  try {
    res = await fetch(routes['meetings.readDocument'].path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
      body: form,
      signal,
    });
  } catch (e) {
    if (signal?.aborted) throw e;
    throw new ApiFailure(0, null);
  }
  if (!res.ok) throw new ApiFailure(res.status, (await res.json().catch(() => null)) as ErrorBody | null);
  return (await res.json()) as ReadMinutesDocumentResponse;
}

/**
 * One idempotency key per distinct request. A retry of the same body (a lost response) reuses the key, so the
 * server replays the first result; a changed body gets a fresh key instead of a 409 "Use a new key".
 */
export function useRequestKey() {
  const ref = useRef<{ sig: string; key: string } | null>(null);
  return useCallback((body: unknown) => {
    const sig = JSON.stringify(body);
    if (ref.current?.sig !== sig) ref.current = { sig, key: newIdempotencyKey() };
    return ref.current.key;
  }, []);
}

export function useMeetingWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => refreshMeetings(qc),
    onError: (e) => {
      if (e instanceof ApiFailure && (e.status === 409 || e.status === 412)) refreshMeetings(qc);
    },
  });
}

export type { Meeting };

// ---------------------------------------------------------------- "Find tasks in these minutes"

export interface FoundTask {
  title: string;
  /** Days from today. */
  due: number;
  /** A team member named in the line (the head may keep it), else null = the client's SPOC. */
  whoId: string | null;
}

const ACTION_RX = /\b(will|should|need to|needs to|must|has to|to send|to share|to prepare|to call|remind|chase|follow up|action)\b/i;
const MONTHS: Record<string, number> = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
/** A whole month word only: "dec" or "december", never the "dec" in "3 decks" or the "mar" in "5 marketing". */
const MONTH = '(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)';
const DAY_MONTH_RX = new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s*(?:of\\s+)?${MONTH}\\b`);
const MONTH_DAY_RX = new RegExp(`\\b${MONTH}\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b`);
/** Escape a name before it goes into a RegExp: a name with "(" or "[" must not throw. */
export const escapeRx = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const WD: Record<string, number> = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

const dayDiff = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);

/** "today", "tomorrow", "by Friday", "15 Oct", "next week", "end of the week", "in 4 days" → days from `today`. */
export function parseDue(low: string, today: string): number | null {
  if (/\btoday\b/.test(low)) return 0;
  if (/\btomorrow\b/.test(low)) return 1;
  const inN = /\bin (\d{1,2}) days?\b/.exec(low);
  if (inN) return Number(inN[1]);
  const dm = DAY_MONTH_RX.exec(low);
  const md = dm ? null : MONTH_DAY_RX.exec(low);
  if (dm || md) {
    const day = Number(dm ? dm[1] : md![2]);
    const month = MONTHS[(dm ? dm[2]! : md![1]!).slice(0, 3)]!;
    const y = Number(today.slice(0, 4));
    const from = Date.parse(`${today}T00:00:00Z`);
    // A date more than a month back means next year's ("2 Jan" said in December).
    const yr = Date.UTC(y, month, Math.min(day, 28)) - from < -31 * 86_400_000 ? y + 1 : y;
    const at = new Date(Date.UTC(yr, month, day));
    // "31 Feb" or "0 Mar" is not a date: no due rather than a day that rolls into the next month.
    if (day < 1 || at.getUTCMonth() !== month) return null;
    return Math.max(0, Math.round((at.getTime() - from) / 86_400_000));
  }
  const w = /\b(mon|tue|wed|thu|fri|sat|sun)(?:day|sday|nesday|rsday|urday)?\b/.exec(low);
  if (w) {
    const now = new Date(`${today}T00:00:00Z`).getUTCDay();
    const n = (WD[w[1]!]! - now + 7) % 7;
    return n === 0 ? 7 : n;
  }
  if (/next week/.test(low)) return 7;
  if (/end of (the )?week/.test(low)) {
    const now = new Date(`${today}T00:00:00Z`).getUTCDay();
    return Math.max(0, (5 - now + 7) % 7);
  }
  return null;
}

/**
 * The prototype's `mtExtract`: headings ("Action items:", "Decisions:", "Attendees:") switch what the following lines
 * are; table rows from a Word or Excel file ("Action | Owner | Due") become tasks; lines with "will / should / must /
 * remind / chase …" become tasks; "decided / agreed" lines become decisions. A line that starts with the client's
 * contact becomes "Chase {contact}: …".
 */
export function findInMinutes(
  text: string,
  opts: { today: string; contactFirst: string | null; people: { id: string; first: string }[] },
): { tasks: FoundTask[]; decisions: string[]; attendees: string } {
  const tasks: FoundTask[] = [];
  const decisions: string[] = [];
  let mode: '' | 'act' | 'dec' | 'neutral' = '';
  let attendees = '';
  const ext = (opts.contactFirst ?? '').toLowerCase();
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  // The person named first in the line is the subject ("Kiran will send this to Priya" → Kiran), not the last in the roster.
  const named = opts.people
    .filter((p) => p.first.trim())
    .map((p) => ({ id: p.id, rx: new RegExp(`(?<![\\p{L}\\p{N}])${escapeRx(p.first.toLowerCase())}(?![\\p{L}\\p{N}])`, 'u') }));
  const whoIn = (l: string) => {
    let who: string | null = null;
    let at = Infinity;
    for (const p of named) {
      const m = p.rx.exec(l);
      if (m && m.index < at) {
        at = m.index;
        who = p.id;
      }
    }
    return who;
  };
  const lines = text
    .split(/\n|(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  for (const s of lines) {
    const low = s.toLowerCase().replace(/^[-•*\d.)\s]+/, '');
    const head = /^(action items?|actions?|next steps?|to ?do|follow[- ]?ups?)\s*[:-]?$/.test(low)
      ? 'act'
      : /^(decisions?|decided|agreed)\s*[:-]?$/.test(low)
        ? 'dec'
        : /^(attendees?|present|participants?|discussion|agenda|summary|notes?|minutes)\s*[:-]?$/.test(low)
          ? 'neutral'
          : null;
    if (head) {
      mode = head;
      continue;
    }
    const att = /^(attendees?|present|participants?)\s*[:-]\s*(.+)$/i.exec(s);
    if (att) {
      attendees = att[2]!.replace(/\.$/, '');
      continue;
    }
    const line = s.replace(/^[-•*]\s*/, '').replace(/^(action|todo|to do|task)\s*[:-]\s*/i, '').replace(/\.$/, '');
    if (line.includes(' | ')) {
      const cells = line.split(' | ').map((x) => x.trim()).filter(Boolean);
      if (cells.length < 2) continue;
      if (cells.every((x) => /^(action|task|item|owner|who|due|date|deadline|by when|status|responsible|sr\.?|no\.?|#)$/i.test(x))) continue;
      const title = [...cells].sort((a, b) => b.length - a.length)[0]!;
      let who: string | null = null;
      let due: number | null = null;
      let isExt = false;
      for (const x of cells) {
        if (x === title) continue;
        const l = x.toLowerCase();
        who = whoIn(l) ?? who;
        if (ext && l.includes(ext)) isExt = true;
        const dd = parseDue(l, opts.today);
        if (dd != null) due = dd;
      }
      const t = isExt ? `Chase ${cap(ext)}: ${title}` : title;
      tasks.push({ title: cap(t), due: due ?? 3, whoId: who });
      continue;
    }
    const lowl = line.toLowerCase();
    if (mode === 'dec' || /\b(decided|agreed|decision)\b/.test(lowl)) {
      decisions.push(line);
      continue;
    }
    const marked = /^[-•*]\s|^(action|todo|to do|task)\s*[:-]/i.test(s);
    if (!(mode === 'act' || marked || ACTION_RX.test(lowl))) continue;
    if (mode === 'neutral' && !ACTION_RX.test(lowl)) continue;
    const isExt = !!ext && new RegExp(`^${escapeRx(ext)}(?![\\p{L}\\p{N}])`, 'u').test(lowl);
    const due = parseDue(lowl, opts.today);
    const t = isExt ? `Chase ${cap(ext)}: ${line}` : line;
    tasks.push({ title: cap(t), due: due ?? 3, whoId: whoIn(lowl) });
  }
  return { tasks, decisions, attendees };
}

/** The prototype's due picker (DUE_OPTS) on real dates: Today, Tomorrow, the next three days by name, Next week, In 2 weeks. */
export function dueOptions(today: string, extra?: number): [number, string][] {
  const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const name = (n: number) => DOW[new Date(`${addDays(today, n)}T00:00:00Z`).getUTCDay()]!;
  const o: [number, string][] = [[0, 'Today'], [1, 'Tomorrow'], [2, name(2)], [3, 'In 3 days'], [4, name(4)], [7, 'Next week'], [14, 'In 2 weeks']];
  if (extra != null && !o.some(([d]) => d === extra)) o.push([extra, `In ${extra} days`]);
  return o.sort((a, b) => a[0] - b[0]);
}

/** "Today / Yesterday / 2 days ago / Last week" → held_on. */
export const WHEN_OPTS: [string, number][] = [['Today', 0], ['Yesterday', -1], ['2 days ago', -2], ['Last week', -7]];

/** "45 min", or how the minutes came when there is no audio length. */
export function lengthLabel(m: MeetingListItem): string {
  if (m.length_minutes != null) return `${m.length_minutes} min`;
  if (m.media?.kind === 'link') return 'Linked';
  if (m.media?.kind === 'audio') return 'Uploaded';
  if (m.transcript_source === 'document') return 'Document';
  if (m.media_kind === 'upload') return 'Minutes file';
  return 'Written';
}

/** The meeting just added in this tab (design frames 19-21 tint its row). Session storage only: a convenience. */
const JUST_ADDED = 'meetings.justAdded';
export function justAddedMeeting(): string | null {
  try {
    return sessionStorage.getItem(JUST_ADDED);
  } catch {
    return null;
  }
}
export function markJustAddedMeeting(id: string): void {
  try {
    sessionStorage.setItem(JUST_ADDED, id);
  } catch {
    /* storage blocked: no tint, nothing else changes */
  }
}
